"use client";
import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { useAuth } from "@/lib/auth-context";
import Logo from "@/components/brand/Logo";

function loginErrorMessage(err: unknown) {
  const e = err as {
    code?: string;
    response?: { status?: number; data?: { detail?: string } };
    message?: string;
  };
  if (e?.code === "SESSION_NOT_RETAINED" || e?.code === "SESSION_COOKIE_BLOCKED") {
    return "Sign-in worked, but your browser did not keep the session. Please try again, or open the site in a normal browser window.";
  }
  if (e?.code === "ECONNABORTED") {
    return "We’re having trouble connecting. Please try again in a moment.";
  }
  if (e?.code === "ERR_NETWORK" || !e?.response) {
    return "Couldn’t connect to OpenHomesAI. If this keeps happening, check your connection or try again later.";
  }
  if (e.response.status === 401) {
    return "Email or password is incorrect.";
  }
  if (e.response.status === 429) {
    return "Too many sign-in attempts. Wait a minute and try again.";
  }
  if (e.response.status && e.response.status >= 500) {
    return "OpenHomesAI sign-in is temporarily unavailable. Please try again shortly.";
  }
  return e.response.data?.detail || "Could not sign in. Please try again.";
}

export default function LoginPage() {
  const [mounted, setMounted] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const router = useRouter();

  useEffect(() => setMounted(true), []);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("email")) setEmail(params.get("email")!);
  }, []);
  if (!mounted) return null;

  const redirectTo = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "").get("redirect") || "/";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      router.push(redirectTo);
    } catch (err: unknown) {
      setError(loginErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page-shell">
      <div className="auth-card auth-card-compact">
        <div className="auth-brand-header">
          <Logo size={32} textSize={16} />
          <span>AI-powered home search</span>
        </div>
        <h1 style={{ fontSize: 24, fontWeight: 760, color: "var(--color-ink)", margin: "0 0 6px", letterSpacing: 0, lineHeight: 1.15 }}>Welcome back</h1>
        <p style={{ fontSize: 14, color: "var(--color-muted)", margin: "0 0 22px", lineHeight: 1.45 }}>Sign in to save homes, watch searches, and ask your AI home search advisor about fit, tradeoffs, and what to verify.</p>

        {error && (
          <div role="alert" style={{ background: "var(--color-error-bg)", border: "1px solid var(--color-error-border)", borderRadius: 10, padding: "11px 13px", marginBottom: 18, fontSize: 13, color: "var(--color-error-text)", lineHeight: 1.45, fontWeight: 650 }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: "var(--color-muted)", marginBottom: 6 }}>Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              className="auth-input"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
            />
          </div>
          <div style={{ marginBottom: 8 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: "var(--color-muted)", marginBottom: 6 }}>Password</label>
            <div style={{ position: "relative" }}>
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                className="auth-input"
                style={{ paddingRight: 44 }}
                autoComplete="current-password"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                style={{ position: "absolute", right: 7, top: "50%", transform: "translateY(-50%)", background: "var(--color-surface-muted)", border: "1px solid var(--color-border-3)", borderRadius: 999, cursor: "pointer", color: "var(--color-brand-dark)", fontSize: 13, fontWeight: 760, fontFamily: "inherit", padding: "7px 10px", lineHeight: 1, minHeight: 34 }}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>
          <div style={{ textAlign: "right", marginBottom: 22 }}>
            <Link href="/auth/forgot-password" style={{ display: "inline-flex", minHeight: 36, alignItems: "center", fontSize: 13, color: "var(--color-brand-dark)", fontWeight: 760 }}>
              Forgot password?
            </Link>
          </div>
          <button type="submit" disabled={loading} className="auth-submit">
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <p style={{ fontSize: 13, color: "var(--color-muted)", textAlign: "center", margin: "18px 0 0" }}>
          No account?{" "}
          <Link href={`/auth/signup${redirectTo !== "/" ? `?redirect=${encodeURIComponent(redirectTo)}` : ""}`} style={{ display: "inline-flex", minHeight: 34, alignItems: "center", color: "var(--color-brand-dark)", fontWeight: 820 }}>Sign up</Link>
        </p>
      </div>
    </div>
  );
}
